import { FC, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  Pagination,
  DataTableSkeleton,
  InlineNotification,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { formatNumber } from 'utils/number';
import { uatsWithoutAmlisLoadableAtom, pageAtom, pageSizeAtom } from './atoms';
import type { UatWithoutAmli } from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

const columnHelper = createColumnHelper<UatWithoutAmli>();

const columns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: 'UAT ID',
    size: 100,
  }),
  columnHelper.accessor('tractName', {
    id: 'tractName',
    header: 'Tract Name',
    size: 250,
  }),
  columnHelper.accessor('gisAcres', {
    id: 'gisAcres',
    header: 'GIS Acres',
    size: 120,
    cell: (info) => formatNumber(info.getValue(), 2, 2, 'decimal', true),
  }),
  columnHelper.accessor('gisUuid', {
    id: 'gisUuid',
    header: 'GIS UUID',
    size: 300,
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created At',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

export const UatsWithoutAmlisPage: FC = () => {
  const loadable = useAtomValue(uatsWithoutAmlisLoadableAtom);
  const [page, setPage] = useAtom(pageAtom);
  const [, setPageSize] = useAtom(pageSizeAtom);

  const data = useMemo(() => {
    return loadable.state === 'hasData' ? (loadable.data?.data ?? []) : [];
  }, [loadable]);

  const pagination =
    loadable.state === 'hasData' ? loadable.data?.pagination : null;
  const stats = loadable.state === 'hasData' ? loadable.data?.stats : null;
  const isLoading = loadable.state === 'loading';
  const error =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error.message
        : 'Failed to fetch data'
      : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[['/admin/dashboard', 'Admin']]}
        title="UATs Without Allocation Manager Line Items"
        actions={null}
      />
      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Unit Allocation Tracts that have no AllocationManagerLineItems. These
          UATs cannot be discovered by TIA for TractIntersectionResult creation
          because UATs are found through the allocation manager data chain. TIRs
          will be created automatically when allocation manager line items
          arrive.
        </div>
      </div>

      {isLoading && <DataTableSkeleton columnCount={5} rowCount={10} />}
      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          hideCloseButton
        />
      )}

      {!isLoading && !error && (
        <>
          {stats && (
            <StatsBar
              stats={[
                {
                  label: 'Total',
                  value: stats.total,
                  tagType: 'gray',
                },
              ]}
            />
          )}

          <DataTable
            data={data}
            columns={columns as ColumnDef<UatWithoutAmli>[]}
            getRowId={(row) => `${row.id}`}
            isLoading={isLoading}
            persistenceKey="admin-uats-without-amlis"
            {...TABLE_CONFIG}
          />

          {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={page}
              pageSize={pagination.pageSize}
              pageSizes={[100, 500, 1000]}
              totalItems={pagination.totalRecords}
              onChange={({
                page: newPage,
                pageSize: newPageSize,
              }: {
                page: number;
                pageSize: number;
              }) => {
                setPage(newPage);
                setPageSize(newPageSize);
              }}
            />
          )}
        </>
      )}
    </>
  );
};
